import React, {
    useEffect,
    useReducer
} from "react";
import {
    getRenderBoxForStartAndDuration,
    NOTE_WIDTH
} from "../../render/renderUtils";
import {
    __DEFAULT_NOTE_RANGES,
    Chord,
    Note,
    NoteDuration,
    TimeSignature,
    Tone,
    ToneIndex
} from "../../model/core";
import './ChordRoll.css';
import { RollRedrawObserver } from "../../utils/RollRedrawObserver";
import { ComputedContextFrame } from "../../model/context";
import { CaretController } from "../../utils/ScrollController";
import { NoteGlyph } from "../rolls/NoteGlyph";
import { ApiFactory } from "../../ApiClient";
import {
    AbstractHorizontalContainer,
    ChordContainer,
    ContainerAtom
} from "../../model/containers";
import {
    PianoRollKeybed
} from "./PianoKeybed";
import _ from "lodash";
import {
    createHarmonicPredicates,
    HarmonicPredicates
} from "../../utils/harmonyHighlight";
import { ChordRollTrack } from "../layouts/ChordRollTrack";
import { PianoRollDisplayMode } from "./PianoRoll";
import { ChordRollStandalone } from "../layouts/ChordRollStandalone";
import { PianoRollBackground } from "../widgets/rolls/PianoRollBackground";
import { ChordSelectionDialog } from "../pickers/ChordSelectionDialog";
import { RydTrackUIState } from "../../model/tracks";
import { ChordInputFloatingDialog } from "../dialogs/ChordInputFloatingDialog";
import { Tonality } from "../../model/scales";


const API = ApiFactory.create();

export function BarSet<T>({
    timeSignature,
    hnc,
    fixedDuration
}: {
    timeSignature: TimeSignature,
    hnc: AbstractHorizontalContainer<T>,
    fixedDuration?: boolean
}) {
    const normalizedBarWidth = timeSignature.numerator * NoteDuration.fromTimeSignature(timeSignature.denominator).value;
    return (
        <>
            <div style={{
                height: '100hh',
                display: 'inline-flex',
                flexDirection: 'row',
                zIndex: 10,
            }} >
                {_.range(1, hnc.getLength() / normalizedBarWidth).map((i) => {
                    const style = {
                        left: `calc((${i * normalizedBarWidth} * var(--note-width) - ${i}px)`,
                        width: '0px'
                    };

                    return <div key={i} className={"ryd-pr--bar-set--delimiter"} style={style} />;
                })}
            </div>
        </>
    );
}

function ToneRollKeybed({
    tonality
}: {
    tonality?: Tonality
}) {
    return (<PianoRollKeybed
        showOctave={false}
        noteRange={__DEFAULT_NOTE_RANGES.tones}
        tonality={tonality}
        style={{
            marginTop: 'var(--chord-roll-collpased-chords-line-height)',
        }}
    />);
}

function ChordExpanded({
    trackId,
    hnc,
    chord,
    partialChordNotes,
    duration,
    start,
    removeNoteCB,
    updateHNC,
}: {
    trackId: number,
    hnc: ChordContainer,
    chord?: Chord,
    partialChordNotes?: number[],
    duration: NoteDuration,
    start: number,
    removeNoteCB?: (start: number, note: number) => void,
    updateHNC: (hnc: ChordContainer) => void,
}) {

    const [noteGroup, setNoteGroup] = React.useState<ToneIndex[]>(chord && chord.tones && chord.tones.length > 0 ?
        chord.tones.map((t) => t.index) : []
    );

    React.useEffect(() => {

        if (!chord) return;

        if (!chord.tones) {
            API.getChord(chord).then((data) => {
                chord.tones = data.tones.map(Tone.fromIndex);
                setNoteGroup(data.tones);
            }).catch((error) => {
                console.error("Error fetching chord data:", error);
            });
        } else {
            setNoteGroup(chord.tones.map((t) => t.index));
        }
    }, []);

    return (<>
        {noteGroup
            .map(Note.fromIndex)
            .map((note: Note, i: number) =>
                <NoteGlyph
                    key={i}
                    trackId={trackId}
                    start={start}
                    note={{
                        step: start,
                        payload: note,
                        duration: duration,
                        velocity: 100
                    }}
                    duration={duration}
                    noteRange={{ min: 0, max: 12 }}
                    topOffset={NOTE_WIDTH}
                    updateNoteDurationCB={() => { }}
                    moveNote={
                        (start: number, note: ContainerAtom<Note>, deltaX: number, deltaY: number) => {
                            console.log('moveNote', { start, note, deltaX, deltaY });
                        }}
                    removeNoteCB={removeNoteCB ? () => removeNoteCB(start, note.index) : () => { }}
                    fixedDuration={true}
                />
            )}
        {(partialChordNotes ? partialChordNotes : []).map(Note.fromIndex)
            .map((note, i) => <NoteGlyph
                key={i}
                trackId={trackId}
                start={start}
                note={{
                    step: start,
                    payload: note,
                    duration: duration,
                    velocity: 100
                }}
                duration={duration}
                noteRange={{ min: 0, max: 12 }}
                topOffset={NOTE_WIDTH}
                updateNoteDurationCB={() => { }}
                moveNote={
                    (start: number, note: ContainerAtom<Note>, deltaX: number, deltaY: number) => {
                        console.log('moveNote', { start, note, deltaX, deltaY });
                    }}
                removeNoteCB={removeNoteCB ? () => removeNoteCB(start, note.index) : () => { }}
                extraClasses={['partial-chord-note']}
                fixedDuration={true}
            />
            )}
    </>);
}

function EmptyCellInput({
    updateChordCB
}: {
    updateChordCB: (chord: Chord) => void
}) {
    const [chordSelectorRefEl, setChordSelectorRefEl] = React.useState<EventTarget & HTMLElement>();
    return <>
        <div
            className={"chord-input--empty"}
            onClick={(e) => {
                setChordSelectorRefEl(e.currentTarget);
            }} />

        {chordSelectorRefEl &&
            <ChordInputFloatingDialog
                chordSelectorRefEl={chordSelectorRefEl}
                updateChordCB={updateChordCB}
                setChordSelectorRefEl={setChordSelectorRefEl} />
        }
    </>;
}

function positionWrapFunctor(
    resolution: NoteDuration,
    ppq: number,
    collpsed?: boolean
): (value: React.JSX.Element, index: number, array: React.JSX.Element[]) => React.JSX.Element {
    return (el, start) => {
        let style: {} = getRenderBoxForStartAndDuration(start, resolution, ppq);
        if (!collpsed) {
            style = { ...style, top: 'var(--note-width)' };
        }
        return <div key={start}
            className="piano-roll-tick"
            style={style}>{el}</div>;
    };
}

export function ChordRollInputLayerCollpased({
    hnc,
    resolution,
    updateChordRollCB
}: {
    hnc: ChordContainer,
    resolution: NoteDuration,
    updateChordRollCB: (start: number, chord: Chord) => void
}) {

    // FIXME: what is this doing?
    const resolutionFactor = resolution.value;
    const positionWrap = positionWrapFunctor(resolution, hnc.getPPQ(), true);

    return (<>
        {_.range(0, hnc.getLength() / resolutionFactor)
            .map((start) => <EmptyCellInput
                key={start}
                updateChordCB={(chord) => updateChordRollCB(start * resolution.value , chord)} />)
            .map(positionWrap)}
    </>);
}

function ChordRollInputLayerNG({
    trackId,
    hnc,
    timeSignature,
    resolution,
    updatePartialChordNoteGroupsCB,
    harmonicPredicates,
}: {
    trackId: number,
    hnc: ChordContainer,
    timeSignature: TimeSignature,
    resolution: NoteDuration,
    updatePartialChordNoteGroupsCB: (start: number, note: Tone) => void,
    harmonicPredicates: HarmonicPredicates,
}) {
    return (
        <div className="piano-roll-input-layer">
            <div style={{
                height: 'var(--chord-roll-collpased-chords-line-height)'
            }}></div>
            <PianoRollBackground
                trackId={trackId}
                hnc={hnc}
                harmonicPredicates={harmonicPredicates}
                timeSignature={timeSignature}
                noteRange={__DEFAULT_NOTE_RANGES.tones}
            />
        </div>
    );
}

const combinedCellTones = (
    hnc: ChordContainer,
    partialChords: PartialChord[],
    start: number
): ToneIndex[] => {
    const partials: ToneIndex[][] = partialChords.map(pc => pc ? pc.tones : []);
    const missingChord = hnc.get(start).length === 0;
    const missingPartials = partials[start] ? partials[start].length === 0 : true;
    if (missingChord && missingPartials) return [];
    if (missingChord) return partials[start];
    if (missingPartials) return _combinedCellTones([], hnc.get(start)[0].payload);
    return _combinedCellTones(partials[start], hnc.get(start)[0].payload);
}

const _combinedCellTones = (partials: ToneIndex[], chord: Chord): ToneIndex[] => {
    return chord.tones.map((t) => t.index).concat(partials);
}

export type PartialChord = {
    tones: ToneIndex[],
    duration: NoteDuration
}

export function ChordRollExpandedView({
    trackId,
    hnc,
    timeSignature,
    resolution,
    tonality,
    updateChordRollCB,
    updateHNC,
    harmonicPredicates,
}: {
    trackId: number,
    hnc: ChordContainer,
    timeSignature: TimeSignature,
    resolution: NoteDuration,
    tonality?: Tonality,
    updateChordRollCB: (start: number, chord: Chord | undefined) => void,
    updateHNC: (hnc: ChordContainer) => void,
    harmonicPredicates: HarmonicPredicates,
}) {
    const [partialChordNoteGroups, setPartialChordNoteGroups] = React.useState<PartialChord[]>([]);

    function updatePartialChordNoteGroups(start: number, note: ToneIndex) {
        const c = hnc.get(start)[0];
        const tones = combinedCellTones(hnc, partialChordNoteGroups, start);
        const newNoteGroups = _.clone(partialChordNoteGroups);
        if (!newNoteGroups[start]) newNoteGroups[start] = { tones: [], duration: c.duration };
        newNoteGroups[start].tones = _.uniq(newNoteGroups[start].tones.concat(tones).concat(note));
        setPartialChordNoteGroups(newNoteGroups);
        updateChordRollCB(start, undefined);
    }

    function clearPartialChord(start: number) {
        const newNoteGroups = _.clone(partialChordNoteGroups);

        newNoteGroups[start].tones = [];
        setPartialChordNoteGroups(newNoteGroups);
    }

    function removePartialChordNote(start: number, note: number) {
        if (hnc.get(start).length > 0) {
            const c = hnc.get(start)[0];
            // if there's a proper chord defined, we check its notes, remove the chord, and add the
            // notes to the partial chord.
            const resolvedChord = c.payload
            const newNoteGroups = _.clone(partialChordNoteGroups);
            if (!newNoteGroups[start]) newNoteGroups[start] = { tones: [], duration: c.duration };
            newNoteGroups[start].tones = _.uniq(newNoteGroups[start].tones
                .concat(resolvedChord.tones.map((t) => t.index)))
                .filter((n) => n !== note);
            setPartialChordNoteGroups(newNoteGroups);
            updateChordRollCB(start, undefined);
            return;
        }

        const newNoteGroups = _.clone(partialChordNoteGroups);
        if (!newNoteGroups[start]) return;
        newNoteGroups[start].tones = newNoteGroups[start].tones.filter((n) => n !== note);
        setPartialChordNoteGroups(newNoteGroups);
    }

    React.useEffect(() => {
        /** When checking if the notes become a chord, we consider the partial notes AND the notes of the
         *  chord that is already defined in the cell.
         */
        _.zip(hnc.getGrid(), partialChordNoteGroups ? partialChordNoteGroups : []).map(([chord, ng], i) => {

            if (!ng || ng.tones.length === 0) return;

            const queryChordsByTones = (tones) => {
                API.getChordIdByNotes(tones).then((chord) => {
                    if (!chord) return;
                    const c = API.loadChord(chord);
                    if (!c) return;
                    updateChordRollCB(i, c);
                    clearPartialChord(i);
                });
            }

            if (chord && chord.length > 0) {
                API.getChord(chord[0].payload).then((data) => {
                    const tones = ng ? data.tones.concat(ng.tones) : data.tones;
                    if (!tones || tones.length < 3) return;
                    queryChordsByTones(tones);
                });
            } else {
                queryChordsByTones(ng.tones);
            }
        });

    }, [partialChordNoteGroups, hnc.getGrid()]);

    return (
        <>
            <ChordRollInputLayerNG
                trackId={trackId}
                hnc={hnc}
                resolution={resolution}
                updatePartialChordNoteGroupsCB={(start, t) => updatePartialChordNoteGroups(start, t.index)}
                timeSignature={timeSignature}
                harmonicPredicates={harmonicPredicates}
            />
            {hnc.getGrid().map((cc, i) => cc.map((chord, j) =>
                <ChordExpanded
                    trackId={trackId}
                    key={`${chord?.payload.root}-${chord?.payload.family}`}
                    chord={chord.payload}
                    duration={chord.duration}
                    start={i}
                    removeNoteCB={removePartialChordNote}
                    updateHNC={updateHNC}
                    hnc={hnc}
                />
            ))}
            {partialChordNoteGroups.map((ng, i) => ng?.tones.map((n, j) =>
                <ChordExpanded
                    trackId={trackId}
                    key={`undef-${i}-${j}`}
                    partialChordNotes={partialChordNoteGroups[i].tones}
                    duration={partialChordNoteGroups[i].duration}
                    start={i}
                    removeNoteCB={removePartialChordNote}
                    updateHNC={updateHNC}
                    hnc={hnc}
                />
            ))}
        </>
    );
}

function ChordRoll({
    trackId,
    line,
    contextFrame,
    caretController,
    updateLineCB,
    redrawObserver,
    mode = 'standalone',
    trackUiState,
    setTrackUIState
}: {
    trackId: number,
    line: ChordContainer,
    contextFrame: ComputedContextFrame,
    caretController?: CaretController,
    updateLineCB: (line: ChordContainer) => void,
    redrawObserver?: RollRedrawObserver,
    mode?: PianoRollDisplayMode,
    trackUiState: RydTrackUIState,
    setTrackUIState?: (state: RydTrackUIState) => void
}) {
    const [newNoteDuration, setNewNoteDuration] = React.useState<NoteDuration>(new NoteDuration(16));
    const [, forceUpdate] = useReducer(x => x + 1, 0);

    const updateChordRollCB = (
        start: number,
        { chord, duration }: { chord: Chord | undefined, duration: NoteDuration }
    ) => {
        if (!chord) {
            line.clear(start);
        } else {
            line.replace(start, {
                step: start,
                duration: duration,
                payload: chord,
                velocity: 100
            });
        }
        updateLineCB?.(line);
    };

    useEffect(() => {
        redrawObserver?.addObserver(forceUpdate);
        return () => {
            redrawObserver?.dropObserver(forceUpdate);
        };
    }, []);

    const harmonicPredicates = createHarmonicPredicates(
        trackUiState.tonalityVisible
            ? contextFrame.tonality
            : undefined
    );

    switch (mode) {
        case 'track':
            return <ChordRollTrack
                trackId={trackId}
                hnc={line}
                contextFrame={contextFrame}
                caretController={caretController}
                updateChordRollCB={updateChordRollCB}
                harmonicPredicates={harmonicPredicates}
                setHnc={updateLineCB}
                trackUiState={trackUiState}
                setTrackUIState={setTrackUIState}
            />;
        case 'standalone':
            return <ChordRollStandalone
                trackId={trackId}
                hnc={line}
                contextFrame={contextFrame}
                caretController={caretController}
                updateChordRollCB={updateChordRollCB}
                harmonicPredicates={harmonicPredicates}
                setHnc={updateLineCB}
                newNoteDuration={newNoteDuration}
                setNewNoteDuration={setNewNoteDuration}
                trackUiState={trackUiState}
                setTrackUIState={setTrackUIState}
            />;
        default:
            throw new Error(`Unknown mode: ${mode}`);
    }
    return null;
}

export default ChordRoll;
export {
    ChordRoll,
    ToneRollKeybed,
    ChordSelectionDialog
};